import { ContentCard } from "@msuite/ui/content-card";
import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Tag } from "@msuite/ui/tag";
import { guardArray } from "@msuite/utils/array";
import { Fragment } from "react";
import { useReturnOrder } from "@/hooks/use-return-order";
import { EditReturnOrderVehiclesModal } from "@/modals/stock/edit-return-order-vehicles-modal";

/** Props Interface */
interface ReturnOrderVehiclesProps {
	returnOrderId: string;
}

/**
 * The lorries fetching the site's material, and the one part of a filed run anybody may change.
 *
 * None is a normal state rather than a missing one: the office raises a run the moment a site is
 * called finished and decides which trucks are going afterwards — so the card says so in words
 * instead of standing empty.
 */
export function ReturnOrderVehicles({ returnOrderId }: ReturnOrderVehiclesProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const editModal = useDisclosure();

	/** Query */
	const { returnOrder } = useReturnOrder(returnOrderId);

	/** Functions */
	async function handleOnComplete() {
		// `return-order-files` among them: changing the fleet reprints the sheet, and the card
		// beside this one would otherwise keep showing the file the new one replaced.
		await qc.invalidateQueries({
			predicate: invalidate(["return-order", "return-orders", "return-order-files"]),
		});
	}

	/** Computed */
	const vehicles = guardArray(returnOrder?.vehicles);

	/** Render */
	return (
		<Fragment>
			<ContentCard
				title={t("stock.return_vehicles")}
				icon={icon("Truck")}
				menuItems={[
					{
						label: t("stock.return_edit_vehicles"),
						onClick: editModal.onOpen,
						icon: icon("Edit"),
					},
				]}
			>
				{vehicles.length === 0 ? (
					<p className="label">{t("stock.return_vehicles_none")}</p>
				) : (
					<div className="flex flex-wrap gap-2">
						{vehicles.map((vehicle) => (
							<Tag
								key={vehicle.id}
								variant="plain"
								color="gray"
							>
								{vehicle.licensePlate}
							</Tag>
						))}
					</div>
				)}
			</ContentCard>
			{editModal.isOpen && (
				<EditReturnOrderVehiclesModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					onComplete={handleOnComplete}
					returnOrderId={returnOrderId}
				/>
			)}
		</Fragment>
	);
}
